import { useParams, useNavigate } from 'react-router-dom'
import { useCourse } from '@/features/courses/hooks/useCourses'
import { useCourseProgress } from '@/features/dashboard/hooks/useCourseProgress'
import { Alert } from '@/components/ui/Alert'
import { useTranslation } from 'react-i18next'

function ConfidenceDots({ confidence }: { confidence: number }) {
  return (
    <div
      className="flex gap-0.5"
      aria-label={`Confiance : ${confidence}/5`}
    >
      {Array.from({ length: 5 }).map((_, i) => {
        let color = 'bg-surface-200'
        if (i < confidence) {
          if (confidence <= 2) color = 'bg-mindeia-error'
          else if (confidence === 3) color = 'bg-mindeia-accent'
          else color = 'bg-mindeia-success'
        }
        return (
          <span
            key={i}
            className={`h-2 w-2 rounded-full ${color}`}
          />
        )
      })}
    </div>
  )
}

export function CourseProgressPage() {
  const { t } = useTranslation()
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data: course, isLoading: courseLoading, isError: courseError } = useCourse(id)
  const {
    data: progress,
    isLoading: progressLoading,
    isError: progressError,
  } = useCourseProgress(id)

  const isLoading = courseLoading || progressLoading
  const isError = courseError || progressError

  const concepts = [...(progress?.concepts ?? [])].sort(
    (a, b) =>
      new Date(b.last_seen_at).getTime() - new Date(a.last_seen_at).getTime(),
  )

  if (isLoading) {
    return (
      <main className="min-h-screen bg-surface-50">
        <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-6">
            <div className="h-4 w-24 rounded bg-surface-200" />
            <div className="h-8 w-1/2 rounded bg-surface-200" />
            <div className="h-28 rounded-xl bg-white border border-surface-200" />
            <div className="grid grid-cols-2 gap-4">
              <div className="h-20 rounded-xl bg-white border border-surface-200" />
              <div className="h-20 rounded-xl bg-white border border-surface-200" />
            </div>
          </div>
        </div>
      </main>
    )
  }

  if (isError) {
    return (
      <main className="min-h-screen bg-surface-50">
        <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
          <Alert variant="error">
            {t('courses.progress_load_error')}
          </Alert>
        </div>
      </main>
    )
  }

  const percent = progress?.completion_percentage ?? 0

  const lastSession = progress?.last_session_at
    ? new Date(progress.last_session_at).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null

  return (
    <main className="min-h-screen bg-surface-50">
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
        <button
          type="button"
          onClick={() => navigate(`/courses/${id}`)}
          className="mb-6 flex items-center gap-1.5 text-sm text-primary-600 hover:underline"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="h-4 w-4"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z"
              clipRule="evenodd"
            />
          </svg>
          {t('courses.back_to_course')}
        </button>

        <div className="mb-6">
          {course?.subject && (
            <span className="mb-2 inline-block rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-medium text-primary-600">
              {course.subject}
            </span>
          )}
          <h1 className="text-2xl font-bold text-neutral-900">{course?.title}</h1>
        </div>

        <div className="mb-6 rounded-xl border border-surface-200 bg-white p-6">
          <div className="mb-4 flex items-center justify-between">
            <span className="text-sm font-medium text-neutral-700">{t('courses.progress_overall')}</span>
            <span className="text-2xl font-bold text-primary-600 tabular-nums">
              {percent}%
            </span>
          </div>
          <div className="h-3 w-full overflow-hidden rounded-full bg-surface-100">
            <div
              className="h-full rounded-full bg-primary-500 transition-all duration-700"
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>

        <div className="mb-6 grid grid-cols-2 gap-4">
          <div className="rounded-xl border border-surface-200 bg-white p-4">
            <p className="text-2xl font-bold text-neutral-900 tabular-nums">
              {progress?.sessions_count ?? 0}
            </p>
            <p className="mt-1 text-sm text-neutral-600">{t('courses.progress_sessions')}</p>
          </div>
          <div className="rounded-xl border border-surface-200 bg-white p-4">
            <p className="text-sm font-semibold text-neutral-900">
              {lastSession ?? 'Aucune'}
            </p>
            <p className="mt-1 text-sm text-neutral-600">{t('courses.progress_last_session')}</p>
          </div>
        </div>

        <section>
          <h2 className="mb-4 text-lg font-semibold text-neutral-900">
            {t('courses.concepts_studied')}
          </h2>
          {concepts.length === 0 ? (
            <div className="rounded-xl border border-dashed border-surface-200 bg-white p-8 text-center">
              <p className="text-sm text-neutral-600 mb-3">
                {t('courses.no_concept')}
              </p>
              <button
                type="button"
                onClick={() => navigate(`/courses/${id}`)}
                className="text-sm font-medium text-primary-600 hover:underline"
              >
                {t('courses.start_session')}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {concepts.map((concept) => (
                <div
                  key={concept.id}
                  className="rounded-xl border border-surface-200 bg-white p-4"
                >
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <p className="text-sm font-medium text-neutral-900 leading-snug">
                      {concept.concept}
                    </p>
                    <ConfidenceDots confidence={concept.confidence} />
                  </div>
                  <p className="text-xs text-neutral-600">
                    Vu le{' '}
                    {new Date(concept.last_seen_at).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'short',
                    })}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </main>
  )
}
